/* VLS / EVS - Sticky add-to-cart bar (Enterprise adaptation).
 *
 * The sticky CTA never adds to cart itself: it clicks the theme's real
 * [name="add"] button, so cart drawer, validations and apps are untouched.
 * The Shopify variant id is the single source of truth. Prices come from
 * Liquid `money` rendered in the snippet - never built in JavaScript.
 *
 * Visibility precedence: ?satc= > window.EVS_SATC_MODE > data-satc-mode.
 * Modes: atc_not_visible (alias always) | after_atc (alias after) | delay |
 * scroll_percent | delay_or_scroll_percent | immediate | disabled.
 * The delay and scroll modes still stay hidden while the main Add to cart is
 * on screen, so the bar never covers the real button.
 * ?satc_delay= and ?satc_scroll= override the numbers for A/B testing.
 *
 * Row 1 badge precedence: ?satc_badge= > window.EVS_SATC_BADGE >
 * data-satc-badge. Values: rating | discount | none. Both markups are in the
 * DOM when the product has a rating, so switching needs no reload.
 */
(function () {
  'use strict';

  var GLOBALS_BOUND = false;
  /* 05/10/2026 (Isma) : le délai se compte depuis l'ouverture de la page (pas depuis le chargement de ce script, ~3-4 s plus tard sur mobile) */
  var startedAt = Date.now() - ((window.performance && performance.now) ? performance.now() : 0);
  var delayFired = false;
  var delayTimer = null;

  function cssEsc(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function urlParam(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }

  function getRefs() {
    var bar = document.querySelector('.vls-satc');
    if (!bar) return null;
    var scope = document.querySelector('.product.js-product');
    var form = scope ? scope.querySelector('.js-product-form-main') : null;
    if (!form) return null;
    return {
      bar: bar,
      scope: scope,
      form: form,
      picker: scope.querySelector('variant-picker'),
      mainAdd: form.querySelector('[name="add"]'),
      hiddenId: form.querySelector('input[name="id"]'),
      select: bar.querySelector('.vls-satc__variant'),
      rating: bar.querySelector('.vls-satc__rating'),
      discount: bar.querySelector('.vls-satc__discount'),
      priceCur: bar.querySelector('.vls-satc__price-cur'),
      priceWas: bar.querySelector('.vls-satc__price-was'),
      addBtn: bar.querySelector('.vls-satc__add'),
      addText: bar.querySelector('.vls-satc__add-text')
    };
  }

  function getData(bar) {
    if (bar.vlsData) return bar.vlsData;
    var out = { variants: [] };
    var el = bar.querySelector('.vls-satc-data');
    if (el) { try { out = JSON.parse(el.textContent); } catch (e) { /* noop */ } }
    bar.vlsData = out;
    return out;
  }

  function variantById(data, id) {
    if (id == null) return null;
    for (var i = 0; i < data.variants.length; i += 1) {
      if (String(data.variants[i].id) === String(id)) return data.variants[i];
    }
    return null;
  }

  function selectedVariantId(refs) {
    return refs.hiddenId ? refs.hiddenId.value : null;
  }

  function mainIsDisabled(refs) {
    var btn = refs.mainAdd;
    return !btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true';
  }

  /* ---- Row 1 badge ---- */

  /* Resolved per call so an A/B tool can flip the attribute or the global
     at any time. Falls back to the discount when the product has no rating
     markup, which is what the Liquid already decided server-side. */
  function currentBadge(refs) {
    var badge = urlParam('satc_badge');
    if (!badge && typeof window.EVS_SATC_BADGE === 'string') badge = window.EVS_SATC_BADGE;
    if (!badge) badge = refs.bar.getAttribute('data-satc-badge');
    if (badge !== 'rating' && badge !== 'discount' && badge !== 'none') badge = 'discount';
    if (badge === 'rating' && !refs.rating) badge = 'discount';
    return badge;
  }

  /* ---- Rendering ---- */

  function renderPrice(refs, data) {
    var v = variantById(data, selectedVariantId(refs)) || data.variants[0];
    if (!v) return;

    if (refs.priceCur) refs.priceCur.innerHTML = v.priceHtml || '';

    var onSale = v.compareAt && v.compareAt > v.price;

    if (refs.priceWas) {
      refs.priceWas.innerHTML = onSale ? (v.compareAtHtml || '') : '';
      refs.priceWas.hidden = !onSale;
    }

    var badge = currentBadge(refs);
    if (refs.rating) refs.rating.hidden = badge !== 'rating';

    if (refs.discount) {
      var pct = onSale ? Math.round((v.compareAt - v.price) * 100 / v.compareAt) : 0;
      if (badge === 'discount' && pct > 0) {
        refs.discount.textContent = '\u2212' + pct + '\u00A0%';
        refs.discount.hidden = false;
      } else {
        refs.discount.hidden = true;
      }
    }
  }

  /* Mirror the theme's own button so sold out / unavailable / choose an option
     stay in sync and translated. */
  function mirrorAddButton(refs) {
    if (!refs.addBtn) return;
    if (mainIsDisabled(refs)) refs.addBtn.setAttribute('disabled', '');
    else refs.addBtn.removeAttribute('disabled');
    var label = ''; if (refs.mainAdd) { var mc = refs.mainAdd.cloneNode(true); var pp = mc.querySelector('.vpc-atc-p'); if (pp) pp.remove(); label = (mc.textContent || '').trim(); } /* 24/09/2026 : sans le prix (déjà affiché dans la barre) */
    if (refs.addText && label) refs.addText.textContent = label;
  }

  function reflectSelect(refs) {
    var id = selectedVariantId(refs);
    if (refs.select && id && refs.select.value !== String(id)) refs.select.value = String(id);
  }

  function syncAll(refs, data) {
    reflectSelect(refs);
    renderPrice(refs, data);
    mirrorAddButton(refs);
  }

  /* ---- Driving the theme's variant picker ---- */

  function setHiddenId(refs, variantId) {
    if (!refs.hiddenId) return;
    refs.hiddenId.value = variantId;
    refs.hiddenId.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function driveVariant(refs, data, variantId) {
    var variant = variantById(data, variantId);
    if (!variant) return;
    var driven = false;
    var selectors = refs.picker ? refs.picker.querySelectorAll('.option-selector') : [];

    if (selectors.length) {
      (variant.options || []).forEach(function (value, index) {
        var selector = selectors[index];
        if (!selector) return;
        if (selector.dataset.selectorType === 'dropdown') {
          var cs = selector.querySelector('custom-select');
          if (cs && typeof cs.selectOption === 'function') {
            var li = cs.querySelector('.custom-select__option[data-value="' + cssEsc(value) + '"]');
            if (li) { cs.selectOption(li); driven = true; return; }
          }
        }
        var radio = selector.querySelector('input.js-option[value="' + cssEsc(value) + '"]');
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          driven = true;
        }
      });
    }

    if (!driven) setHiddenId(refs, variantId);

    window.setTimeout(function () {
      var fresh = getRefs();
      if (!fresh) return;
      if (String(selectedVariantId(fresh)) !== String(variantId)) setHiddenId(fresh, variantId);
      syncAll(fresh, data);
    }, 80);

    syncAll(refs, data);
  }

  /* ---- Visibility ---- */

  function normalizeMode(mode) {
    if (mode === 'always') return 'atc_not_visible';
    if (mode === 'after') return 'after_atc';
    return mode;
  }

  function currentMode(bar) {
    var mode = urlParam('satc');
    if (!mode && typeof window.EVS_SATC_MODE === 'string') mode = window.EVS_SATC_MODE;
    if (!mode) mode = bar.getAttribute('data-satc-mode');
    return normalizeMode(mode || 'atc_not_visible');
  }

  function numberOption(bar, urlKey, attr, fallback) {
    var raw = urlParam(urlKey);
    if (raw == null) raw = bar.getAttribute(attr);
    var n = parseFloat(raw);
    return isNaN(n) ? fallback : n;
  }

  function deviceAllows(bar) {
    var isDesktop = window.innerWidth >= 769;
    var attr = bar.getAttribute(isDesktop ? 'data-satc-desktop' : 'data-satc-mobile');
    return attr !== 'false';
  }

  function reservedSpace(bar) {
    var h = bar.offsetHeight || 0;
    return h ? h + (window.innerWidth >= 769 ? 20 : 0) : 0;
  }

  function mainOffScreen(refs) {
    if (!refs.mainAdd) return false;
    var rect = refs.mainAdd.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    /* 27/09/2026 (Isma) : sur ordinateur la barre est une carte en bas à droite ; elle ne s'affiche que si le vrai bouton n'est plus à l'écran (avant : réservait 216 px et couvrait le bloc d'achat dès le 1er écran) */
    var freeBottom = vh - (window.innerWidth >= 769 ? 0 : reservedSpace(refs.bar));
    var shown = Math.min(rect.bottom, freeBottom) - Math.max(rect.top, 0);
    var visible = rect.height > 0 && shown >= Math.min(rect.height * 0.6, 40);
    return !visible;
  }

  function scrollPercent() {
    var doc = document.documentElement;
    var vh = window.innerHeight || doc.clientHeight;
    var total = Math.max(doc.scrollHeight - vh, 1);
    return Math.min(100, Math.max(0, (window.pageYOffset || doc.scrollTop) * 100 / total));
  }

  function armDelay(bar) {
    if (delayTimer || delayFired) return;
    var seconds = numberOption(bar, 'satc_delay', 'data-satc-delay', 3);
    var remaining = Math.max(0, seconds * 1000 - (Date.now() - startedAt));
    delayTimer = window.setTimeout(function () {
      delayFired = true;
      delayTimer = null;
      scheduleUpdate();
    }, remaining);
  }

  function updateVisibility() {
    var refs = getRefs();
    if (!refs) return;

    var bar = refs.bar;
    var mode = currentMode(bar);
    var show;

    if (mode === 'disabled' || !deviceAllows(bar)) {
      show = false;
    } else if (mode === 'immediate') {
      show = true;
    } else if (!refs.mainAdd) {
      show = false;
    } else if (mode === 'after_atc') {
      show = refs.mainAdd.getBoundingClientRect().bottom < 0;
    } else if (mode === 'delay' || mode === 'scroll_percent' || mode === 'delay_or_scroll_percent') {
      var triggered = false;
      if (mode !== 'scroll_percent') {
        armDelay(bar);
        triggered = delayFired;
      }
      if (!triggered && mode !== 'delay') {
        triggered = scrollPercent() >= numberOption(bar, 'satc_scroll', 'data-satc-scroll', 25);
      }
      /* Never cover the real button, even once triggered. */
      show = triggered && mainOffScreen(refs);
    } else {
      show = mainOffScreen(refs);
    }

    bar.classList.toggle('is-visible', show);
  }

  var ticking = false;
  function scheduleUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      updateVisibility();
    });
  }

  /* ---- Init ---- */

  function init() {
    var refs = getRefs();
    if (!refs) return;
    var data = getData(refs.bar);

    /* The bar and the form are replaced together on a section re-render,
       so compare the actual nodes instead of using a flag. */
    if (refs.bar.vlsBoundForm === refs.form) {
      syncAll(refs, data);
      scheduleUpdate();
      return;
    }
    refs.bar.vlsBoundForm = refs.form;

    if (refs.addBtn) {
      refs.addBtn.addEventListener('click', function (evt) {
        evt.preventDefault();
        var live = getRefs();
        if (!live || mainIsDisabled(live)) return;
        live.mainAdd.click();
      });
    }

    if (refs.select) {
      refs.select.addEventListener('change', function () {
        var live = getRefs();
        if (live) driveVariant(live, data, live.select.value);
      });
    }

    if (refs.mainAdd) {
      if (refs.bar.vlsObserver) refs.bar.vlsObserver.disconnect();
      refs.bar.vlsObserver = new MutationObserver(function () {
        var live = getRefs();
        if (live) mirrorAddButton(live);
      });
      refs.bar.vlsObserver.observe(refs.mainAdd, {
        attributes: true, childList: true, subtree: true, characterData: true
      });
    }

    /* An A/B tool may rewrite data-satc-badge after load: re-render row 1. */
    if (refs.bar.vlsBadgeObserver) refs.bar.vlsBadgeObserver.disconnect();
    refs.bar.vlsBadgeObserver = new MutationObserver(function () {
      var live = getRefs();
      if (live) renderPrice(live, getData(live.bar));
    });
    refs.bar.vlsBadgeObserver.observe(refs.bar, {
      attributes: true, attributeFilter: ['data-satc-badge']
    });

    if (!GLOBALS_BOUND) {
      GLOBALS_BOUND = true;
      document.addEventListener('on:variant:change', function () {
        var live = getRefs();
        if (!live) return;
        syncAll(live, getData(live.bar));
        scheduleUpdate();
      });
      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', scheduleUpdate, { passive: true });
      window.addEventListener('orientationchange', scheduleUpdate);
      document.addEventListener('on:cart:change', scheduleUpdate);
      window.EVS_SATC_REFRESH = scheduleUpdate;
    }

    if (delayTimer) { window.clearTimeout(delayTimer); delayTimer = null; }
    delayFired = false;
    /* 05/10/2026 : startedAt garde l'heure d'ouverture de la page (voir en haut) */

    syncAll(refs, data);
    refs.bar.hidden = false;
    updateVisibility();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
}());
